import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	inject,
	OnDestroy,
	OnInit,
} from '@angular/core';
import { AuRightSidebarService } from '@core/services/au-right-sidebar.service';
import { Observable, Subject } from 'rxjs';
import { mergeMap, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { CmmsPages, ICmmsState } from '@app/pages/cmms/state/cmms.reducer';
import { iconEnum } from '@app/configs/model/IconEnum';
import { AuDialogService } from '@core/services/au-dialog.service';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { editWorkOrder } from '@app/pages/cmms/components/work-order/state/work-order.selectors';
import {
	addWorkOrderExternalData,
	setWorkOrderForEditing,
	startDeletingWorkOrder,
	startUpdateWorkOrder,
} from '@app/pages/cmms/components/work-order/state/work-order.actions';
import { ICmmsAssignee } from '@app/pages/cmms/interfaces/assignee.interface';
import { RightSidebarNavigationService } from '@layout/au-right-sidebar/right-sidebar-navigation.service';
import { AuErrorAdapter, AuUtilsFunctions } from '@app/shared';
import { IconSnackBarService } from '@core/services/icon-snack-bar.service';
import { ACTIVE_PAGE_CONFIG } from '@app/pages/cmms/configs/active-page.config';
import { SetRightSidebarComponentContext } from '@layout/state/layout.actions';
import { IncidentsService } from '@app/pages/cmms/services/incidents.service';
import { RequestsService } from '@app/pages/cmms/services/requests.service';
import { IIncident } from '@app/pages/cmms/interfaces/incidents.interface';
import { IRequest } from '@app/pages/cmms/interfaces/request.interfaces';
import { ICMMSColumn } from '@shared/ui-components/au-table-layout/utils/interfaces';
import {
	attachedEntityTableConfig,
	sidebarHeaders,
} from '@app/pages/cmms/configs/cmms-table-configs';
import { WorkOrdersService } from '@app/pages/cmms/services/work-orders.service';
import { CmmsCommentsService } from '@app/pages/cmms/services/cmms-comments.service';
import { ICMMSLocation } from '@app/pages/cmms/interfaces/cmms-global.interfaces';
import { ICmmsAsset } from '@app/pages/cmms/interfaces/assets.interfaces';
import { CommonModule } from '@angular/common';
import { CMMS_STATUSES } from '@app/pages/cmms/configs/statuses-by-entity';
import { FilesService } from '@app/pages/cmms/services/files.service';
import { RightSidebarAliasesEnum } from '@layout/au-right-sidebar/au-right-sidebar-component-aliases';
import { IAuCommentEditEvent } from '@shared/ui-components/au-comments/i-au.comment';
import { fileTypesConfig } from '@app/pages/cmms/configs/file-filter.config';
import { CmmsSharedService } from '@app/pages/cmms/services/cmms-shared.service';
import { TableType } from '@app/pages/cmms/types';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CmmsEntityNameComponent } from '../../shared-form-group-controls/cmms-entity-name/cmms-entity-name.component';
import { MatRippleModule } from '@angular/material/core';
import { MatMenuModule } from '@angular/material/menu';
import { AuAvatarWithNameModule } from '@app/shared/ui-components';
import { AuPipesModule } from '@app/shared/pipes/au-pipes.module';
import { CmmsEntityStatusComponent } from '../../shared-form-group-controls/cmms-entity-status/cmms-entity-status.component';
import { CmmsLocationsSectionComponent } from '../../shared-form-group-controls/cmms-locations-section/cmms-locations-section.component';
import { CmmsPriorityLevelComponent } from '../../shared-form-group-controls/cmms-priorirty-level/cmms-priority-level.component';
import { CmmsEntityCategoryComponent } from '../../shared-form-group-controls/cmms-entity-category/cmms-entity-category.component';
import { CmmsAssigeeAndApproverComponent } from '../../shared-form-group-controls/cmms-assignees-and-approver/cmms-assigee-and-approver.component';
import { CmmsDatePickerComponent } from '../../shared-form-group-controls/cmms-date-picker/cmms-date-picker.component';
import { CmmsLabelsSectionComponent } from '../../shared-form-group-controls/cmms-labels-section/cmms-labels-section.component';
import { CmmsScheduleSectionComponent } from '../../shared-form-group-controls/cmms-schedule-section/cmms-schedule-section.component';
import { CmmsDescriptionComponent } from '../../shared-form-group-controls/cmms-description/cmms-description.component';
import { MaintenanceSectionComponent } from '../../maintenance-section/maintenance-section.component';
import { AuMaintenanceSelectorComponent } from '../../au-maintenance-selector/au-maintenance-selector.component';
import { AuCmmsCostComponent } from '../../au-cmms-cost/au-cmms-cost.component';
import { WorkOrderChecklistComponent } from '../work-order-checklist/work-order-checklist.component';
import { EntitySectionComponent } from '../../entity-section/entity-section.component';
import { AssetsSectionComponent } from '../../assets-section/assets-section.component';
import { IAttachFiles } from '@app/shared/ui-components/au-file-loader/au-file-loader.interfaces';
import { MatButtonModule } from '@angular/material/button';
import { OpenDtFromWorkspaceComponent } from '@app/pages/cmms/components/shared-form-group-controls/cmms-open-dt-from-workspace/open-dt-from-workspace.component';
import { AuCommentsComponent } from '@app/shared/ui-components/au-comments/au-comments.component';
import { ImageSectionComponent } from '../../files/image-section/image-section.component';
import { CommentInputComponent } from '@app/shared/ui-components/au-comments/comment-input/comment-input.component';
import { EmailService } from '@app/pages/settings/email-service/email-service.service';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';
import {
	IAttachedAsset,
	IWorkOrder,
	IWorkOrderEditPayload,
	IWorkOrderMaintenance,
} from '@app/domain/cmms-types/workorder-types';
import { AuFileLoaderComponent } from '@app/shared/ui-components/au-file-loader/au-file-loader.component';
import {
	AttachNewQuickEntityComponent,
	ActionType,
} from '@app/shared/entity-workspace-components/quick-entity/attach-new-quick-entity/attach-new-quick-entity.component';

@Component({
	selector: 'edit-work-order',
	templateUrl: './edit-work-order.component.html',
	styleUrls: ['./edit-work-order.component.scss'],
	imports: [
		CommonModule,
		MatIconModule,
		MatProgressSpinnerModule,
		CmmsEntityNameComponent,
		MatRippleModule,
		MatMenuModule,
		AuAvatarWithNameModule,
		AuPipesModule,
		CmmsEntityStatusComponent,
		CmmsLocationsSectionComponent,
		CmmsPriorityLevelComponent,
		CmmsEntityCategoryComponent,
		CmmsAssigeeAndApproverComponent,
		CmmsDatePickerComponent,
		CmmsLabelsSectionComponent,
		CmmsScheduleSectionComponent,
		CmmsDescriptionComponent,
		MaintenanceSectionComponent,
		AuMaintenanceSelectorComponent,
		AuCmmsCostComponent,
		WorkOrderChecklistComponent,
		EntitySectionComponent,
		AssetsSectionComponent,
		AuFileLoaderComponent,
		AuCommentsComponent,
		MatButtonModule,
		OpenDtFromWorkspaceComponent,
		ImageSectionComponent,
		CommentInputComponent,
		NgxTolgeeModule,
		AttachNewQuickEntityComponent,
	],
})
export class EditWorkOrderComponent implements OnInit, OnDestroy {
	private readonly fb = inject(FormBuilder);
	private readonly sidebarService = inject(AuRightSidebarService);
	private readonly store$ = inject(Store<ICmmsState>);
	private readonly dialogService = inject(AuDialogService);
	private readonly notify = inject(IconSnackBarService);
	private readonly sidebarNavigation = inject(RightSidebarNavigationService);
	private readonly incidentsService = inject(IncidentsService);
	private readonly requestService = inject(RequestsService);
	private readonly cd = inject(ChangeDetectorRef);
	private readonly workOrdersService = inject(WorkOrdersService);
	private readonly commentService = inject(CmmsCommentsService);
	private readonly filesService = inject(FilesService);
	private readonly cmmsSharedService = inject(CmmsSharedService);
	private readonly el = inject(ElementRef);
	private readonly emailService = inject(EmailService);
	private readonly translateService = inject(TranslateService);

	workOrderForm: FormGroup = this.fb.group({});
	workOrder: IWorkOrder;
	attachedIncidents: IIncident[] = [];
	attachedWO: IWorkOrder[] = [];
	attachedRequest: IRequest[] = [];
	attachedAssets: IAttachedAsset[] = [];
	attachedFiles: IAttachFiles[] = [];

	readonly iconEnum = iconEnum;
	readonly cmmsPages = CmmsPages;
	readonly tableType = TableType;
	readonly EntityType = ActionType;

	readonly tableConfig: ICMMSColumn[] = attachedEntityTableConfig;
	readonly workOrderStatuses = CMMS_STATUSES.WORK_ORDERS;
	deadline: Date | string;
	locationAncestry: Partial<ICMMSLocation>[] = [];
	isFullscreen$: Observable<boolean>;

	private destroy$: Subject<void> = new Subject<void>();

	ngOnInit(): void {
		this.isFullscreen$ = this.sidebarService.sidebarFullscreen$;
		this.getWorkOrderForEdit();
		this.trackSidebarCloseAction();
	}

	scrollTop() {
		const anchor = document.getElementById('top-anchor');
		anchor.scrollIntoView({ block: 'end' });
	}

	trackSidebarCloseAction(): void {
		this.sidebarService.sidebarIsClosing$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.sidebarService.closeAndClearPortal();
		});
	}
	reloadPage() {
		this.editWorkOrder({ name: this.workOrder.name }, true);
	}

	getWorkOrderForEdit() {
		this.store$
			.select(editWorkOrder)
			.pipe(
				takeUntil(this.destroy$),
				mergeMap(workOrder => this.workOrdersService.getWorkOrderByPk(workOrder.pk))
			)
			.subscribe(workOrder => {
				this.workOrder = workOrder;
				if (workOrder.incidentWorkOrders) {
					this.attachedIncidents = [
						...this.incidentsService.getIncidentsData(workOrder.incidentWorkOrders)
							.incidents,
					];
				}

				if (workOrder.workOrders) {
					this.attachedWO = [...workOrder.workOrders];
				}

				if (workOrder.requestWorkOrders) {
					this.attachedRequest = [
						...this.requestService.getRequestsData(workOrder.requestWorkOrders).requests,
					];
				}

				if (this.workOrder.files) {
					this.attachedFiles = this.workOrder.files.filter(
						file => file.importConfirmed || !file.importId
					);
				}

				this.attachedAssets = this.workOrder.assets || [];
				this.cd.markForCheck();
			});
	}

	approverUpdate(approver: Partial<ICmmsAssignee>) {
		this.editWorkOrder({
			approver: approver.psId,
			approverType: approver.contentType,
		});
	}

	assigneeUpdate(assignee: Partial<ICmmsAssignee>) {
		this.editWorkOrder({
			assignee: assignee.psId,
			assigneeType: assignee.contentType,
		});
	}

	checklistUpdate(checklistsTemplate: string) {
		if (!checklistsTemplate) {
			return;
		}

		const checklistId = this.workOrder.checklists[0]?.id;

		// TODO Look how this optimistic update will work as due to the fact
		//  that in some cases there is no checklist id in work order object but we anyway
		//  will update that after the response will succeed
		const updatedChecklists = [{ template: checklistsTemplate, id: checklistId }];
		this.workOrder = { ...this.workOrder, checklists: updatedChecklists };

		if (checklistId) {
			this.updateExistingChecklist(checklistsTemplate, checklistId);
		} else {
			this.createNewChecklist(checklistsTemplate);
		}
	}

	private updateExistingChecklist(checklistsTemplate: string, id: string) {
		this.workOrdersService.updateChecklist(checklistsTemplate, id).subscribe({
			next: res => {
				if (!res.success) {
					this.notify.error(
						this.translateService.instant('checklist_status_update_error')
					);
				}
			},
			error: error => {
				this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
			},
		});
	}

	private createNewChecklist(checklistsTemplate: string) {
		this.editWorkOrder({
			checklists: [{ template: checklistsTemplate }],
		});
	}

	deleteChecklist() {
		this.workOrdersService.deleteChecklist(this.workOrder.checklists[0].id).subscribe({
			next: res => {
				if (!res.success) {
					this.notify.error(
						this.translateService.instant('checklist_status_delete_error')
					);
				}

				this.workOrder = { ...this.workOrder, checklists: [] };
				this.cd.markForCheck();
				this.notify.success(
					this.translateService.instant('checklist_status_delete_success')
				);
			},
			error: error => {
				this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
			},
		});
	}

	editWorkOrder(partialWorkOrder: Partial<IWorkOrderEditPayload>, overrideCheck = false) {
		if (this.checkIfWorkOrderDataChanged(partialWorkOrder) || overrideCheck) {
			// if repeatable WO is completed, BE created another one with the same name.
			// And we need to show it to user. So here is a flag which I catch in the effects
			if (this.repeatableWOCompleted(partialWorkOrder)) {
				partialWorkOrder['needUpdateTable'] = true;
			}
			this.store$.dispatch(
				startUpdateWorkOrder({
					workOrder: { pk: this.workOrder.pk, ...partialWorkOrder },
				})
			);
		}
	}

	updateWokOrderMaintenance(payload: Partial<IWorkOrderMaintenance>) {
		this.editWorkOrder(payload);
	}

	completeWorkOrder() {
		this.editWorkOrder({ status: 'COMPLETED' });
		this.scrollTop();
	}

	private repeatableWOCompleted(workOrderData: Partial<IWorkOrderEditPayload>) {
		return workOrderData.status === 'COMPLETED' && this.workOrder.routineData;
	}

	filesUpdates(fileIds) {
		this.store$.dispatch(
			startUpdateWorkOrder({
				workOrder: {
					pk: this.workOrder.pk,
					files: fileIds,
				},
			})
		);
		this.filesService.getUpdatedFiles();
	}

	filesDeleted({ ids, onSuccess }: { ids: string[]; onSuccess: () => void }) {
		const workOrderCopy = AuUtilsFunctions.deepCopyObject(this.workOrder);

		workOrderCopy.files = this.workOrder.files.filter(file => !ids.includes(file.pk));

		this.filesService
			.unlinkAttachedElements({
				pk: ids[0],
				workOrders: this.workOrder.pk,
			})
			.subscribe(() => {
				onSuccess();
				this.store$.dispatch(setWorkOrderForEditing({ workOrderEdit: workOrderCopy }));
				this.notify.success(
					`${this.translateService.instant('work_order')} ${this.translateService.instant('has_been_updated_successfully')}`
				);
			});
	}

	checkIfWorkOrderDataChanged(newWorkOrder: Partial<IWorkOrderEditPayload>) {
		let hasChanges = false;
		Object.keys(newWorkOrder).map(key => {
			if (newWorkOrder[key] !== this.workOrder[key]) {
				hasChanges = true;
			}
		});

		return hasChanges;
	}

	workOrderDeleteBtnHandler(): void {
		const dialog = this.dialogService.openAlertDialog({
			title: this.translateService.instant('dialog_delete_work_order_title'),
			description: `<p class="au-cmms-alert-text">${this.translateService.instant('dialog_delete_data_loss_warning')}</p>`,
			actionBtnText: this.translateService.instant('delete'),
		});

		this.dialogService.closedByEnter(dialog).subscribe(approved => {
			if (approved) {
				this.deleteWorkOrder();
			}
		});
	}

	copyWorkOrder() {
		this.setNavigationConfigForHistory();

		this.store$.dispatch(
			SetRightSidebarComponentContext({
				componentContext: {
					actionType: 'COPY',
				},
			})
		);

		this.store$.dispatch(
			addWorkOrderExternalData({ ...structuredClone(this.workOrder) })
		);

		this.sidebarNavigation.navigateTo(CmmsPages.newWorkOrder, {
			...ACTIVE_PAGE_CONFIG[CmmsPages.wo].sidebarConfig,
			title: { text: 'copy_of', endfix: this.workOrder.name },
			actionBtnName: 'submit_copy',
		});
	}

	navigateTo(page: CmmsPages) {
		this.setNavigationConfigForHistory();

		this.store$.dispatch(
			SetRightSidebarComponentContext({
				componentContext: {
					currentEntityForEdit: this.workOrder,
					editEntityType: 'WO',
					actionType: 'EDIT',
				},
			})
		);

		this.cmmsSharedService.setExternalDataForNewComponent(page, this.workOrder);

		this.sidebarNavigation.navigateTo(page, {
			...ACTIVE_PAGE_CONFIG[page].sidebarConfig,
			title: {
				text: ACTIVE_PAGE_CONFIG[page].sidebarConfig.title.toString(),
				endfix: this.workOrder.workOrderId,
			},
		});
	}

	navigateToExist(pageData: { page: CmmsPages; tableType: TableType }) {
		this.setNavigationConfigForHistory();

		this.store$.dispatch(
			SetRightSidebarComponentContext({
				componentContext: {
					tableTypeForExistingEntity: pageData.tableType,
					actionType: 'EDIT',
					currentEntityForEdit: this.workOrder,
					editEntityType: 'WO',
				},
			})
		);

		this.sidebarNavigation.navigateTo(
			CmmsPages.existingEntities,
			sidebarHeaders[pageData.tableType]
		);
	}

	setNavigationConfigForHistory() {
		this.sidebarNavigation.setInitialComponent({
			initialComponent: RightSidebarAliasesEnum.editWorkOrder,
			config: {
				...ACTIVE_PAGE_CONFIG[CmmsPages.editWorkOrder].sidebarConfig,
				title: {
					text: ACTIVE_PAGE_CONFIG[
						CmmsPages.editWorkOrder
					].sidebarConfig.title.toString(),
					endfix: this.workOrder.workOrderId,
				},
			},
			initialPage: CmmsPages.editWorkOrder,
		});
	}

	openAssets() {
		this.setNavigationConfigForHistory();

		this.store$.dispatch(
			SetRightSidebarComponentContext({
				componentContext: {
					tableTypeForExistingEntity: TableType.ASSETS,
					currentEntityForEdit: this.workOrder,
					editEntityType: 'WO',
					actionType: 'EDIT',
				},
			})
		);

		this.sidebarNavigation.navigateTo(
			CmmsPages.existingEntities,
			sidebarHeaders[TableType.ASSETS]
		);
	}

	viewSelected(item: { data: any; entityType: CmmsPages }) {
		this.sidebarService.viewCurrentEntity(
			EditWorkOrderComponent,
			CmmsPages.editWorkOrder,
			item,
			this.workOrder
		);
	}

	deleteWorkOrder() {
		this.store$.dispatch(startDeletingWorkOrder({ pk: this.workOrder.pk }));
	}

	//TODO This is still a piece of shit in a current implementation of state management, refactor it when possible
	unlinkItem(
		itemType: 'incidents' | 'requests' | 'assets' | 'workOrders',
		itemArrayName,
		primaryKey: string | Partial<ICmmsAsset>
	): void {
		this.workOrdersService
			.unlinkAttachedElements({
				pk: this.workOrder.pk,
				[itemType]: primaryKey,
			})
			.subscribe({
				next: () => {
					this[itemArrayName] = this[itemArrayName].filter(
						item => item.pk !== primaryKey
					);
					this.notifySuccess();
					this.cd.markForCheck();
				},
				error: () => this.notifyError(),
			});
	}

	unlinkIncident(incident: IIncident) {
		this.unlinkItem('incidents', 'attachedIncidents', incident.pk);
	}

	unlinkRequest(request: IRequest) {
		this.unlinkItem('requests', 'attachedRequest', request.pk);
	}

	unlinkWorkOrder(workOrder: IWorkOrder) {
		this.unlinkItem('workOrders', 'attachedWO', workOrder.pk);
	}

	unlinkAsset(asset: ICmmsAsset) {
		const filteredAssets = this.attachedAssets?.length
			? this.attachedAssets.filter(attachedAsset => attachedAsset.pk !== asset.pk)
			: [];

		this.workOrdersService
			.updateWorkOrder({
				pk: this.workOrder.pk,
				assets: filteredAssets.map(attachedAsset => {
					return { id: attachedAsset.pk, name: attachedAsset.name };
				}),
			})
			.subscribe({
				next: workOrder => {
					this.attachedAssets = workOrder.data.editWorkOrder.workOrder.assets || [];
					this.workOrder = workOrder.data.editWorkOrder.workOrder;
					this.notifySuccess();
					this.cd.markForCheck();
				},
				error: () => this.notifyError(),
			});
	}

	addChildControl(name: string, control: FormControl) {
		this.workOrderForm.addControl(name, control);
	}

	submitComment(comment: {
		commentText: string;
		sendAsEmail: boolean;
		mentions: string[];
	}) {
		this.commentService
			.addCommentToEntity('workOrder', this.workOrder.pk, comment)
			.subscribe({
				next: res => {
					if (!res.success) {
						this.notify.error(this.translateService.instant('error_comment_save'));
					}

					const woCopy = AuUtilsFunctions.deepCopyObject(this.workOrder);
					woCopy.comments.unshift(res.comment);

					this.workOrder = woCopy;
					this.cd.markForCheck();
					//we will rewrite the workspace in near future, temp fix.
					setTimeout(() => {
						this.scrollToBottom();
					}, 100);
				},
				error: error => {
					this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
				},
			});
	}
	// TODO Think how to move this to a comment service or any other way of making comment
	//  related methods generic as they are used in all entities
	deleteCommentConfirmation($event: { commentId: string }) {
		const dialog = this.dialogService.openAlertDialog({
			title: this.translateService.instant('dialog_delete_comment_title'),
			description: this.translateService.instant('dialog_delete_comment_description'),
			actionBtnText: this.translateService.instant('delete'),
		});

		this.dialogService.closedByEnter(dialog).subscribe(approved => {
			if (approved) {
				this.deleteComment($event.commentId);
			}
		});
	}

	deleteComment(commentId: string) {
		this.commentService.deleteComment(commentId).subscribe({
			next: res => {
				if (!res.success) {
					this.notify.error(this.translateService.instant('error_comment_delete'));
				}

				const woCopy = AuUtilsFunctions.deepCopyObject(this.workOrder);
				woCopy.comments = this.workOrder.comments.filter(
					comment => comment.id !== commentId
				);

				this.workOrder = woCopy;
				this.cd.markForCheck();
			},
			error: error => {
				this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
			},
		});
	}

	editComment($event: IAuCommentEditEvent) {
		this.commentService.editEntityComment(
			$event.commentId,
			$event.commentText,
			this.workOrder,
			(updatedEntityItem: IWorkOrder) => {
				this.workOrder = updatedEntityItem;
				this.cd.markForCheck();
			}
		);
	}

	// TODO You can move 3 this methods into the one.
	//  Just find out how to name it well
	deadlineChanged($event: Date) {
		this.store$.dispatch(
			startUpdateWorkOrder({
				workOrder: {
					pk: this.workOrder.pk,
					deadline: $event,
					name: this.workOrder.name,
				},
			})
		);
	}

	locationsChanged(locations: Partial<ICMMSLocation>[]) {
		this.store$.dispatch(
			startUpdateWorkOrder({
				workOrder: {
					pk: this.workOrder.pk,
					name: this.workOrder.name,
					locations,
				},
			})
		);
	}

	openMessageComponent() {
		this.emailService.setMessageExternalData(
			this.workOrder,
			`Tiltak: ${this.workOrder.workOrderId}`,
			this.locationAncestry,
			this.attachedFiles
		);
		this.navigateTo(CmmsPages.messages);
	}

	scrollToBottom() {
		const activeElement = this.el.nativeElement.querySelector(
			'.edit-work-order-container'
		);

		if (activeElement) {
			activeElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
		}
	}

	private notifySuccess() {
		this.notify.success(
			`${this.translateService.instant('work_order')} ${this.translateService.instant('has_been_updated_successfully')}`
		);
	}

	private notifyError() {
		this.notify.error(
			`${this.translateService.instant('work_order')} ${this.translateService.instant('failed_to_update')}`
		);
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	protected readonly fileTypesConfig = fileTypesConfig;
}
